// Phase 5Z.28 — Redesigned Dashboard with KPI cards, Expiry warnings, Queues, Quick Actions, Activity
// Phase E (26.05.2026) — Pinnable widgets via DashboardPinPicker
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Truck, ClipboardCheck, AlertTriangle, FileText, Receipt, Plus,
  ShieldAlert, Calendar, Activity, Clock, ArrowRight, AlertCircle, KeyRound,
  Upload, CreditCard, ArrowLeftRight, Pin,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import ModuleHint from "@/components/ModuleHint";
import FirmStatsCards from "@/components/FirmStatsCards";
import DashboardPinPicker from "@/components/DashboardPinPicker";
import { loadPins, fetchServerPins } from "@/lib/dashboardWidgets";
import { fmtDate, fmtCur, fmtDateTime } from "@/lib/format";
import { EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState(null);
  const [activity, setActivity] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pins, setPins] = useState(() => loadPins(user));

  useEffect(() => {
    setPins(loadPins(user));
    // Phase E-2 — Pull authoritative pins from server (overwrites local
    // if the user pinned something on another device). Fast-path render
    // already uses the cached set above so this is a silent refresh.
    if (user?.id) {
      fetchServerPins(user).then((srv) => setPins(srv)).catch(() => {});
    }
  }, [user]);
  const hasPin = (id) => pins.has(id);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/overview").then(r => setOverview(r.data)).catch(() => {}),
      api.get("/dashboard/expiring-vehicles?days=30").then(r => setExpiring(r.data)).catch(() => {}),
      api.get("/dashboard/expiring-driver-docs?days=60").then(r => setExpiringDocs(r.data)).catch(() => {}),
      api.get("/dashboard/recent-activity?limit=8").then(r => setActivity(r.data)).catch(() => {}),
    ]);
  }, []);

  const k = overview?.kpis || {};
  const q = overview?.queues || {};
  const f = overview?.finance || {};
  const e = overview?.expiry_alerts || {};
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 11) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
  })();

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <ModuleHint moduleKey="dashboard" />
      {/* Hero */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-50">
            {greeting}{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
          </h1>
          <p className="text-sm text-zinc-400 mt-0.5">{fmtDate(overview?.today)} · Live-Übersicht</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPickerOpen(true)}
            data-testid="dashboard-pin-open"
            title="Dashboard anpassen — Karten an-/abpinnen"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md transition-colors"
          >
            <Pin className="w-3.5 h-3.5" /> Anpassen
          </button>
          <QuickActions />
        </div>
      </div>

      <DashboardPinPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onChange={setPins}
      />

      {/* Per-firm overview cards (hidden when only 1 firm exists) */}
      {hasPin("firm-stats") && <FirmStatsCards />}

      {/* Phase 5Z.36 Phase 3 — HR Smart Widget for managers */}
      {hasPin("hr-summary") && <HrManagerCard />}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI testid="kpi-active-drivers" icon={Users} label="Aktive Fahrer" value={k.drivers_active} subtitle={`${k.portal_enabled || 0} Portal aktiv`} />
        <KPI testid="kpi-active-vehicles" icon={Truck} label="Aktive Fahrzeuge" value={k.vehicles_active} subtitle={`${k.active_handovers || 0} ausgegeben`} tone="sky" />
        <KPI testid="kpi-portal-today" icon={Clock} label="Heute erfasst" value={k.portal_entries_today} subtitle="via Fahrer-Portal" tone="emerald" />
        <KPI testid="kpi-pending-absences" icon={Calendar} label="Offene Anträge" value={q.pending_absences} subtitle="Abwesenheiten" tone={q.pending_absences > 0 ? "amber" : "zinc"} link="/abwesenheiten" />
        <KPI testid="kpi-overdue" icon={AlertTriangle} label="Überfällig" value={f.overdue_count} subtitle={fmtCur(f.overdue_amount)} tone={f.overdue_count > 0 ? "red" : "zinc"} link="/finanzen?tab=rechnungen" />
        <KPI testid="kpi-expiring" icon={ShieldAlert} label="Bald ablaufend" value={(e.red_count || 0) + (e.yellow_count || 0)} subtitle={`${e.red_count || 0} kritisch · ${e.yellow_count || 0} bald`} tone={(e.red_count || 0) > 0 ? "red" : ((e.yellow_count || 0) > 0 ? "amber" : "zinc")} />
      </div>

      {(hasPin("expiring-vehicles") || hasPin("queues") || hasPin("finance")) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Expiry widget */}
          {hasPin("expiring-vehicles") && (
          <div className={`${(hasPin("queues") || hasPin("finance")) ? "lg:col-span-2" : "lg:col-span-3"} rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden`} data-testid="dashboard-expiring-widget">
            <header className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-500" />
                <h2 className="font-display font-semibold text-zinc-100">Fahrzeug-Termine — überfällig &amp; bald ablaufend</h2>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                {expiring?.overdue_count ? (
                  <span className="text-red-400 font-semibold">{expiring.overdue_count} überfällig</span>
                ) : null}
                {expiring?.overdue_count && expiring?.soon_count ? <span className="text-zinc-600 mx-1">·</span> : null}
                {expiring?.soon_count ? (
                  <span className="text-amber-400">{expiring.soon_count} in 30 Tagen</span>
                ) : null}
                {!expiring?.overdue_count && !expiring?.soon_count ? <span>{expiring?.total ?? 0} Einträge</span> : null}
              </span>
            </header>
            {expiring && expiring.items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">Alle Termine sind im grünen Bereich. ✓</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-900/80 backdrop-blur">
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="px-3 py-2 text-left">Kennzeichen</th>
                      <th className="px-3 py-2 text-left">Fahrzeug</th>
                      <th className="px-3 py-2 text-left">Termin</th>
                      <th className="px-3 py-2 text-left">Datum</th>
                      <th className="px-3 py-2 text-right">Tage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(expiring?.items || []).slice(0, 100).map((it, i) => (
                      <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`expiring-row-${i}`}>
                        <td className="px-3 py-2">
                          <Link to={`/fuhrpark`} className="font-mono text-xs text-amber-300 hover:text-amber-200">{it.license_plate}</Link>
                        </td>
                        <td className="px-3 py-2 text-zinc-300">{it.label}</td>
                        <td className="px-3 py-2 text-xs text-zinc-300">{it.field_label}</td>
                        <td className="px-3 py-2 font-mono text-xs text-zinc-400">{fmtDate(it.expiry_date)}</td>
                        <td className="px-3 py-2 text-right">
                          <UrgencyBadge urgency={it.urgency} days={it.days_left} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Queues + Finance side panel */}
          {(hasPin("queues") || hasPin("finance")) && (
          <div className={`${hasPin("expiring-vehicles") ? "" : "lg:col-span-3"} space-y-4`}>
            {hasPin("queues") && (
            <Card title="Genehmigungs-Queue" icon={ClipboardCheck} testid="dashboard-queues">
              <QueueRow label="Abwesenheiten" count={q.pending_absences || 0} link="/abwesenheiten" />
              <QueueRow label="Profil-Anfragen" count={q.pending_profile || 0} link="/profilanfragen" />
              <QueueRow label="Zeit-Anfragen" count={q.pending_time_changes || 0} link="/audit" />
            </Card>
            )}

            {hasPin("finance") && (
            <Card title="Finanzen" icon={Receipt} testid="dashboard-finance">
              <div className="space-y-2.5 px-4 py-3">
                <Stat label="Offene Rechnungen" value={f.open_invoices || 0} sub={fmtCur(f.open_invoice_amount)} />
                <div className={(f.overdue_count || 0) > 0 ? "border-l-2 border-red-500/60 pl-2.5" : ""}>
                  <Stat label="Überfällig" value={f.overdue_count || 0} sub={fmtCur(f.overdue_amount)} tone={(f.overdue_count || 0) > 0 ? "red" : "zinc"} />
                </div>
                <Link to="/finanzen?tab=rechnungen" className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-200 mt-1">
                  Alle Rechnungen ansehen <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </Card>
            )}
          </div>
          )}
        </div>
      )}

      {/* Phase G — Expiring Driver Documents */}
      {hasPin("expiring-driver-docs") && (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid="dashboard-expiring-driver-docs">
        <header className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-500" />
            <h2 className="font-display font-semibold text-zinc-100">Fahrer-Dokumente — überfällig &amp; bald ablaufend</h2>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            {expiringDocs?.overdue_count ? (
              <span className="text-red-400 font-semibold">{expiringDocs.overdue_count} überfällig</span>
            ) : null}
            {expiringDocs?.overdue_count && expiringDocs?.soon_count ? <span className="text-zinc-600 mx-1">·</span> : null}
            {expiringDocs?.soon_count ? (
              <span className="text-amber-400">{expiringDocs.soon_count} in 30 Tagen</span>
            ) : null}
            {!expiringDocs?.overdue_count && !expiringDocs?.soon_count ? <span>{expiringDocs?.total ?? 0} Einträge</span> : null}
          </span>
        </header>
        {expiringDocs && expiringDocs.items.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-zinc-500">Keine ablaufenden Fahrer-Dokumente in den nächsten {expiringDocs.days} Tagen. ✓</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-900/80 backdrop-blur">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2 text-left">Fahrer</th>
                  <th className="px-3 py-2 text-left">Dokument</th>
                  <th className="px-3 py-2 text-left">Datum</th>
                  <th className="px-3 py-2 text-right">Tage</th>
                </tr>
              </thead>
              <tbody>
                {(expiringDocs?.items || []).slice(0, 100).map((it, i) => (
                  <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`expiring-driver-doc-row-${i}`}>
                    <td className="px-3 py-2">
                      <Link to={`/mitarbeiter/${it.employee_id}`} className="text-amber-300 hover:text-amber-200">
                        {it.employee_name}
                      </Link>
                      {it.personal_nr ? <span className="ml-2 font-mono text-[10px] text-zinc-500">#{it.personal_nr}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-zinc-300">
                      {it.subject || it.doc_type}
                      {it.source === "employee_field" ? (
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] uppercase tracking-wider text-zinc-400">Stammdaten</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-400">{fmtDate(it.expiry_date)}</td>
                    <td className="px-3 py-2 text-right">
                      <UrgencyBadge urgency={it.urgency} days={it.days_left} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Recent activity */}
      {hasPin("activity") && (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid="dashboard-activity">
        <header className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
          <Activity className="w-4 h-4 text-amber-500" />
          <h2 className="font-display font-semibold text-zinc-100">Letzte Aktivitäten</h2>
        </header>
        <div className="divide-y divide-zinc-800">
          {(activity?.items || []).slice(0, 8).map((a, i) => (
            <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-xs hover:bg-zinc-900/40">
              <span className="font-mono text-zinc-500 w-32 shrink-0">{fmtDateTime(a.timestamp)}</span>
              <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 capitalize text-[10px] tracking-wider">{a.action}</span>
              <span className="text-zinc-400">{a.module}</span>
              <span className="flex-1 text-zinc-300">{a.user_email || "—"}</span>
              <span className="text-[10px] text-zinc-500 capitalize">{a.role || ""}</span>
            </div>
          ))}
          {!activity?.items?.length && (
            <EmptyState label="Keine Aktivitäten in den letzten Tagen." />
          )}
        </div>
      </div>
      )}

      {/* Phase 32b — Performance per Firma (Phase G: switched from per department) */}
      {hasPin("departments") && <FirmPerformance />}

      {/* Phase G+ Phase 4 (19.06.2026) — Lohnlauf-Vorschau-Widget */}
      {hasPin("payroll-preview") && <PayrollPreviewCard />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Phase G+ Phase 4 — Lohnlauf-Vorschau pro Fahrer (Live aus payroll_engine)
// Zeigt für den aktuellen Monat: Bruttosumme, Diäten, Überstunden,
// Anomalien je Fahrer — damit fehlende Tage / fehlende KV-Kategorie
// sofort vor dem Monatsabschluss auffallen.
// ────────────────────────────────────────────────────────────────────
function PayrollPreviewCard() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyAnomalies, setOnlyAnomalies] = useState(false);
  const [detailEid, setDetailEid] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [lastSnapshot, setLastSnapshot] = useState(null);

  // Phase G+ Phase 7 — Excel export + snapshot-lock
  const exportLohnliste = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await api.post(`/payroll-lohnliste/export/${year}/${month}`, null, {
        responseType: "blob",
      });
      const snapId = r.headers?.["x-snapshot-id"];
      const snapHash = r.headers?.["x-snapshot-hash"];
      const fname = `lohnliste_${year}-${String(month).padStart(2,"0")}.xlsx`;
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      setLastSnapshot({ id: snapId, hash: snapHash, kind: "lohnliste", at: new Date().toISOString() });
      toast.success(`Lohnliste exportiert · Snapshot ${snapId?.slice(0,8)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  // Phase G+ Phase 8 — Zulagen-Export (Long-form für Lohnverrechner)
  const exportZulagen = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await api.post(
        `/payroll-lohnliste/zulagen-export/${year}/${month}`, null,
        { responseType: "blob" },
      );
      const snapId = r.headers?.["x-snapshot-id"];
      const snapHash = r.headers?.["x-snapshot-hash"];
      const fname = `zulagen_${year}-${String(month).padStart(2,"0")}.xlsx`;
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      setLastSnapshot({ id: snapId, hash: snapHash, kind: "zulagen", at: new Date().toISOString() });
      toast.success(`Zulagen-Export erstellt · Snapshot ${snapId?.slice(0,8)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zulagen-Export fehlgeschlagen");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/payroll-preview?year=${year}&month=${month}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  if (!data && !loading) return null;
  const items = (data?.items || []).filter(x =>
    !onlyAnomalies || (x.anomalies && x.anomalies.length > 0)
  );
  const totals = data?.totals || {};
  const monthNames = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];

  return (
    <section data-testid="dashboard-payroll-preview" className="space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-50">Lohnlauf-Vorschau</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Voraussichtliche Bruttosumme · Diäten ({fmtCur(data?.dieten_per_hour || 2.26)}/h aufgerundet)
            · Überstunden je Fahrer. <b>Klicke einen Namen</b> für Detail-Ansicht.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            data-testid="payroll-preview-month"
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1"
          >
            {monthNames.map((n, i) => (
              <option key={i} value={i + 1}>{n}</option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || today.getFullYear())}
            data-testid="payroll-preview-year"
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 w-20"
          />
          <label className="inline-flex items-center gap-1 text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyAnomalies}
              onChange={(e) => setOnlyAnomalies(e.target.checked)}
              data-testid="payroll-preview-only-anomalies"
            />
            Nur Anomalien
          </label>
          <button
            onClick={exportLohnliste}
            disabled={exporting}
            data-testid="payroll-export-btn"
            className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white px-3 py-1.5 rounded transition-colors"
            title="Lohnliste als Excel exportieren · setzt Snapshot-Lock"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {exporting ? "Exportiere…" : "Lohnliste Excel"}
          </button>
          <button
            onClick={exportZulagen}
            disabled={exporting}
            data-testid="payroll-zulagen-btn"
            className="inline-flex items-center gap-1.5 text-xs bg-sky-600 hover:bg-sky-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white px-3 py-1.5 rounded transition-colors"
            title="Zulagen-Export für Lohnverrechner (BMD/Sage)"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {exporting ? "Exportiere…" : "Zulagen-Export"}
          </button>
        </div>
      </div>

      {lastSnapshot && (
        <div
          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200"
          data-testid="payroll-last-snapshot"
        >
          ✓ Letzter Export ({lastSnapshot.kind === "zulagen" ? "Zulagen-Export" : "Lohnliste"}):
          Snapshot <code className="font-mono">{lastSnapshot.id?.slice(0,8)}</code>
          {" "}· Hash <code className="font-mono">{lastSnapshot.hash?.slice(0,12)}…</code>
        </div>
      )}

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <PayrollTotalCard label="Bruttosumme" value={fmtCur(totals.brutto_sum || 0)} tone="emerald" testid="payroll-total-brutto" />
        <PayrollTotalCard label="Diäten gesamt" value={fmtCur(totals.dieten_sum || 0)} tone="sky" testid="payroll-total-dieten" />
        <PayrollTotalCard label="Akonto-Abzüge" value={fmtCur(totals.advances_deduction_sum || 0)} tone={(totals.advances_deduction_sum || 0) > 0 ? "amber" : "zinc"} testid="payroll-total-advances" />
        <PayrollTotalCard label="Überstunden" value={`${(totals.overtime_hours_sum || 0).toLocaleString("de-AT", {minimumFractionDigits: 1, maximumFractionDigits: 1})} h`} tone="amber" testid="payroll-total-overtime" />
        <PayrollTotalCard label="Mit Anomalien" value={`${totals.anomalies_count || 0} / ${totals.drivers_count || 0}`} tone={totals.anomalies_count > 0 ? "red" : "zinc"} testid="payroll-total-anomalies" />
      </div>

      {/* Driver rows */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        {loading && (
          <div className="p-4 text-xs text-zinc-500" data-testid="payroll-preview-loading">Lade Vorschau…</div>
        )}
        {!loading && items.length === 0 && (
          <EmptyState
            text={onlyAnomalies ? "Keine Anomalien — alles sauber 🎉" : "Keine Fahrer im Scope"}
          />
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-900/50 text-zinc-400">
                <th className="text-left px-3 py-2">Fahrer</th>
                <th className="text-right px-3 py-2">Tage</th>
                <th className="text-right px-3 py-2">Std.</th>
                <th className="text-right px-3 py-2">ÜStd.</th>
                <th className="text-right px-3 py-2">Diäten</th>
                <th className="text-right px-3 py-2">Brutto</th>
                <th className="text-right px-3 py-2">Akonto</th>
                <th className="text-right px-3 py-2">Netto</th>
                <th className="text-left px-3 py-2">Anomalien</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 50).map((it) => (
                <tr
                  key={it.employee_id}
                  className="border-t border-zinc-800 hover:bg-zinc-900/40 cursor-pointer"
                  data-testid={`payroll-row-${it.employee_id}`}
                  onClick={() => setDetailEid(it.employee_id)}
                >
                  <td className="px-3 py-2 text-zinc-100">{it.employee_name || "—"}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">
                    {it.days_worked} / {it.expected_working_days}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-300">
                    {(it.work_hours || 0).toLocaleString("de-AT", {minimumFractionDigits: 1, maximumFractionDigits: 1})}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className={it.overtime_minutes > 60 * 15 ? "text-amber-400" : "text-zinc-300"}>
                      {(it.overtime_hours || 0).toLocaleString("de-AT", {minimumFractionDigits: 1, maximumFractionDigits: 1})}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-300">{fmtCur(it.dieten_sum_eur || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-400">{fmtCur(it.projected_brutto_eur || 0)}</td>
                  <td className="px-3 py-2 text-right">
                    {(it.advances_deduction_eur || 0) > 0 ? (
                      <span
                        className="font-mono text-amber-400"
                        title={(it.advances || []).map(a => `${a.label}: ${a.amount_eur} €`).join(" · ")}
                      >
                        − {fmtCur(it.advances_deduction_eur)}
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-100">{fmtCur(it.net_after_advances_eur || it.projected_brutto_eur || 0)}</td>
                  <td className="px-3 py-2">
                    {(it.anomalies && it.anomalies.length > 0) ? (
                      <span className="inline-flex items-center gap-1 text-rose-400 text-[11px]">
                        <AlertCircle className="w-3 h-3" />
                        <span title={it.anomalies.join(" · ")}>
                          {it.anomalies[0]}{it.anomalies.length > 1 ? ` (+${it.anomalies.length - 1})` : ""}
                        </span>
                      </span>
                    ) : (
                      <span className="text-emerald-500 text-[11px]">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {items.length > 50 && (
          <div className="px-3 py-2 text-[11px] text-zinc-500 border-t border-zinc-800">
            … {items.length - 50} weitere Fahrer ausgeblendet. Filtere oder öffne Lohnabrechnung für Vollansicht.
          </div>
        )}
      </div>

      {/* Phase G+ Phase 6 — Lohn-Detail-Modal pro Fahrer */}
      {detailEid && (
        <PayrollDetailModal
          eid={detailEid} year={year} month={month}
          onClose={() => setDetailEid(null)}
          onGoToProfile={() => { navigate(`/mitarbeiter/${detailEid}`); setDetailEid(null); }}
        />
      )}
    </section>
  );
}

// Phase G+ Phase 6 (19.06.2026) — Lohn-Detail-Modal.
// Shows the full Lohnarten breakdown from the payroll-engine.
function PayrollDetailModal({ eid, year, month, onClose, onGoToProfile }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/payroll/compute/${eid}/${year}/${month}`)
      .then(r => setData(r.data)).catch(() => setData({ error: "fehlgeschlagen" }));
  }, [eid, year, month]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="payroll-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-950">
          <div>
            <div className="font-display text-base font-semibold text-zinc-100">{data?.employee_name || "Lade…"}</div>
            <div className="text-[11px] text-zinc-500">Lohn-Detail · {month}/{year}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onGoToProfile} className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded">
              → Profil
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">✕</button>
          </div>
        </div>

        {!data && <div className="p-6 text-xs text-zinc-500">Lade…</div>}
        {data?.error && <div className="p-6 text-rose-400">{data.error}</div>}
        {data?.summary && (
          <div className="p-5 space-y-4">
            {/* Summary KPIs */}
            <div className="grid grid-cols-3 gap-3 text-xs">
              <KPIMini label="Tage" value={`${data.summary.days_worked}/${data.expected_working_days}`}/>
              <KPIMini label="Std." value={data.summary.work_hours.toLocaleString("de-AT", {maximumFractionDigits:1})}/>
              <KPIMini label="ÜStd." value={`${data.summary.overtime_25_minutes}+${data.summary.overtime_50_minutes} min`}/>
              <KPIMini label="Diäten" value={`${data.summary.dieten_hours}h × 2,26 = ${fmtCur(data.summary.dieten_sum_eur)}`}/>
              <KPIMini label="Brutto (geschätzt)" value={fmtCur(data.summary.brutto_total_eur)} tone="emerald"/>
              <KPIMini label="Netto (geschätzt)" value={fmtCur(data.summary.netto_estimate_eur)} tone="emerald"/>
              <KPIMini label="Akonto-Abzug" value={fmtCur(data.summary.advances_deduction_eur)} tone="amber"/>
              <KPIMini label="Unbezahlte Abz." value={fmtCur(data.summary.unpaid_deduction_eur)} tone="amber"/>
              <KPIMini label="Auszahlbar" value={fmtCur(data.summary.payout_eur)} tone="emerald"/>
            </div>

            {/* Lohnarten table */}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mb-1">Lohnarten</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-400 text-left">
                    <th className="px-2 py-1">Code</th>
                    <th className="px-2 py-1">Bezeichnung</th>
                    <th className="px-2 py-1 text-right">Betrag</th>
                    <th className="px-2 py-1">Notiz</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.lines || []).map((l, i) => (
                    <tr key={i} className="border-t border-zinc-800">
                      <td className="px-2 py-1 font-mono text-zinc-500">{l.code}</td>
                      <td className="px-2 py-1 text-zinc-200">{l.label}</td>
                      <td className={`px-2 py-1 text-right font-mono ${l.amount_eur < 0 ? "text-rose-400" : "text-zinc-100"}`}>{fmtCur(l.amount_eur)}</td>
                      <td className="px-2 py-1 text-zinc-500">{l.note}</td>
                    </tr>
                  ))}
                  {(!data.lines || data.lines.length === 0) && (
                    <tr><td colSpan={4} className="px-2 py-3 text-center text-zinc-500">Keine Lohnarten — bitte Stammdaten prüfen.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Anomalies */}
            {data.anomalies?.length > 0 && (
              <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-200">
                <b>Anomalien:</b>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {data.anomalies.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}

            {/* Abwesenheiten */}
            {(data.summary.paid_absence_days > 0 || data.summary.unpaid_absence_days > 0) && (
              <div className="text-xs text-zinc-400">
                <b>Abwesenheiten im Monat:</b>{" "}
                {data.summary.paid_absence_days > 0 && <>bezahlt {data.summary.paid_absence_days} Tage</>}
                {data.summary.unpaid_absence_days > 0 && <>, unbezahlt {data.summary.unpaid_absence_days} Tage</>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KPIMini({ label, value, tone }) {
  const toneCls = { emerald: "text-emerald-300", amber: "text-amber-300" }[tone] || "text-zinc-100";
  return (
    <div className="bg-zinc-900 rounded p-2">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-0.5 font-display font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}

function PayrollTotalCard({ label, value, tone = "zinc", testid }) {
  const colorByTone = {
    emerald: "text-emerald-400",
    sky:     "text-sky-400",
    amber:   "text-amber-400",
    red:     "text-rose-400",
    zinc:    "text-zinc-200",
  };
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-xl font-bold ${colorByTone[tone] || colorByTone.zinc}`}>
        {value}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Phase G (28.05.2026) — Firm Performance Cards
// Mirrors the old Department widget but groups by company. Reuses the
// `DeptCard` component below since the backend `/dashboard/firm-stats`
// returns the same per-bucket shape.
// ────────────────────────────────────────────────────────────────────
function FirmPerformance() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/dashboard/firm-stats").then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  if (items.length === 0) return null;

  return (
    <section data-testid="dashboard-firms" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-50">Performance pro Firma</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Live-Übersicht: Fahrzeuge, Mitarbeiter, Compliance &amp; offene Aufgaben pro Firma. <b>Klicke eine Zelle</b> um direkt zur gefilterten Liste zu springen.
          </p>
        </div>
        <div className="text-[10px] text-zinc-500">{items.length} Firmen</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((d) => <DeptCard key={d.id || d.name} d={d} navigate={navigate} />)}
      </div>
    </section>
  );
}

function DeptCard({ d, navigate }) {
  const totalEmpActive = (d.employees_drivers_active || 0) + (d.employees_office_active || 0);
  const pickerlOk = d.pickerl_ok || 0;
  const pickerlWarn = (d.pickerl_soon || 0) + (d.pickerl_expired || 0) + (d.pickerl_missing || 0);
  const pickerlPct = d.vehicles_total > 0 ? Math.round((pickerlOk / d.vehicles_total) * 100) : 0;

  // Compute health tone
  let tone = "emerald";
  if (d.tasks_overdue > 0 || d.pickerl_expired > 5) tone = "red";
  else if (pickerlWarn > 0 || d.tasks_open > 5 || d.employees_krank + d.employees_urlaub > 5) tone = "amber";

  const accent = {
    emerald: "border-emerald-500/30",
    amber: "border-amber-500/40",
    red: "border-red-500/40",
  }[tone];

  // Phase G (28.05.2026): widget is now per-firm; navigate with firm_id filter
  // so the target list shows entries of THIS company. Keep the legacy
  // `department_id` query for entries that came from the old endpoint.
  const filt = d.id ? `&firm_id=${encodeURIComponent(d.id)}` : "";
  const goVeh = (extra = "") => navigate(`/fuhrpark?_t=${Date.now()}${filt}${extra}`);
  const goEmp = (extra = "") => navigate(`/mitarbeiter?_t=${Date.now()}${filt}${extra}`);
  const goTasks = (extra = "") => navigate(`/operations/aufgaben?_t=${Date.now()}${filt}${extra}`);

  return (
    <div className={`rounded-lg border ${accent} bg-zinc-950 p-4`} data-testid={`firm-card-${d.id || "none"}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-zinc-100 truncate" title={d.name}>{d.name}</h3>
        <HealthDot tone={tone} />
      </div>

      {/* Vehicles */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
          <span><Truck className="w-3 h-3 inline mr-1" />Fahrzeuge</span>
          <span>{d.vehicles_total}</span>
        </div>
        <div className="grid grid-cols-4 gap-1 text-[11px]">
          <Pill label="Aktiv" value={d.vehicles_active} tone="emerald" onClick={() => goVeh("&status=aktiv")} testid={`pill-veh-active-${d.id || "none"}`} />
          <Pill label="Werkstatt" value={d.vehicles_werkstatt} tone="amber" onClick={() => goVeh("&status=werkstatt")} testid={`pill-veh-werkstatt-${d.id || "none"}`} />
          <Pill label="Parkend" value={d.vehicles_parkend} tone="zinc" onClick={() => goVeh("&status=parkend")} />
          <Pill label="Inaktiv" value={d.vehicles_inactive} tone="zinc" onClick={() => goVeh("&status=inaktiv")} />
        </div>
      </div>

      {/* Employees */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
          <span><Users className="w-3 h-3 inline mr-1" />Mitarbeiter</span>
          <span>{d.employees_total} · aktiv {totalEmpActive}</span>
        </div>
        <div className="grid grid-cols-4 gap-1 text-[11px]">
          <Pill label="Fahrer" value={d.employees_drivers_active} tone="sky" onClick={() => goEmp("&status=aktiv")} />
          <Pill label="Office" value={d.employees_office_active} tone="emerald" onClick={() => goEmp("&status=aktiv")} />
          <Pill label="Krank" value={d.employees_krank} tone="amber" onClick={() => goEmp("&status=krank")} />
          <Pill label="Urlaub" value={d.employees_urlaub} tone="amber" onClick={() => goEmp("&status=urlaub")} />
        </div>
      </div>

      {/* Compliance */}
      {d.vehicles_total > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
            <span><ShieldAlert className="w-3 h-3 inline mr-1" />§57a Pickerl</span>
            <span className={pickerlPct < 60 ? "text-red-400" : pickerlPct < 90 ? "text-amber-400" : "text-emerald-400"}>{pickerlPct}% ok</span>
          </div>
          <div className="grid grid-cols-4 gap-1 text-[11px]">
            <Pill label="Gültig" value={d.pickerl_ok} tone="emerald" onClick={() => goVeh("")} />
            <Pill label="≤30T" value={d.pickerl_soon} tone="amber" onClick={() => goVeh("")} />
            <Pill label="Abgelf." value={d.pickerl_expired} tone="red" onClick={() => goVeh("")} />
            <Pill label="Fehlt" value={d.pickerl_missing} tone="zinc" onClick={() => goVeh("")} />
          </div>
        </div>
      )}

      {/* Tasks */}
      <div className="grid grid-cols-3 gap-1 text-[11px] pt-2 border-t border-zinc-800">
        <Pill label="Offen" value={d.tasks_open} tone={d.tasks_open ? "sky" : "zinc"} onClick={() => goTasks("")} />
        <Pill label="Überfällig" value={d.tasks_overdue} tone={d.tasks_overdue ? "red" : "zinc"} onClick={() => goTasks("&overdue=1")} />
        <Pill label="Fahrer wart." value={d.tasks_fahrer_pending} tone={d.tasks_fahrer_pending ? "amber" : "zinc"} onClick={() => goTasks("&target_audience=fahrer")} />
      </div>
    </div>
  );
}

function HealthDot({ tone }) {
  const map = {
    emerald: "bg-emerald-400 shadow-emerald-400/50",
    amber: "bg-amber-400 shadow-amber-400/50",
    red: "bg-red-400 shadow-red-400/50",
  };
  return <span className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px_2px] ${map[tone]}`} />;
}

function Pill({ label, value, tone = "zinc", onClick, testid }) {
  const tones = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    zinc: "bg-zinc-800/50 text-zinc-400 border-zinc-700",
  };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`rounded border ${tones[tone]} px-1.5 py-1 text-center transition-transform ${onClick ? "hover:brightness-125 hover:-translate-y-px focus:outline-none focus:ring-1 focus:ring-amber-400 cursor-pointer" : ""}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-90">{label}</div>
      <div className="text-sm font-bold tabular-nums">{value || 0}</div>
    </Tag>
  );
}

// ────────────────────────────────────────────────────────────────────
// Phase G (28.05.2026) — DataCompleteness removed at user's request.
// The endpoint /dashboard/data-completeness was also removed from the
// backend. If reintroduced later, re-add the widget id to dashboardWidgets.js.
// ────────────────────────────────────────────────────────────────────

function QuickActions() {
  const { can, role, isAdmin } = usePermissions();
  // Finance actions: only visible to admin/GF or roles with create-rights on
  // finanzen/auftraege/rechnungen modules. Phase 5Z.30.
  const canAuftrag = isAdmin || role === "geschaeftsfuehrung" ||
    can("finanzen_auftraege", "create") || can("finanzen", "create");
  const canRechnung = isAdmin || role === "geschaeftsfuehrung" ||
    can("finanzen_rechnungen", "create") || can("finanzen", "create");
  const canFahrzeug = isAdmin || can("fuhrpark", "create");
  const canMitarbeiter = isAdmin || can("mitarbeiter", "create");
  const canDokument = isAdmin || can("dokumente", "create");
  const canKarte = isAdmin || can("karten", "create");
  const canUebergabe = isAdmin || can("fuhrpark", "create");

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="quick-actions">
      {canAuftrag && (
        <Link to="/finanzen?tab=auftraege" data-testid="qa-auftrag" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600/15 border border-blue-500/30 text-blue-300 hover:bg-blue-600/25 text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> Auftrag
        </Link>
      )}
      {canRechnung && (
        <Link to="/finanzen?tab=rechnungen" data-testid="qa-rechnung" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/25 text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> Rechnung
        </Link>
      )}
      {canFahrzeug && (
        <Link to="/fuhrpark" data-testid="qa-fahrzeug" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-600/15 border border-amber-500/30 text-amber-300 hover:bg-amber-600/25 text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> Fahrzeug
        </Link>
      )}
      {canMitarbeiter && (
        <Link to="/mitarbeiter" data-testid="qa-mitarbeiter" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> Mitarbeiter
        </Link>
      )}
      {canDokument && (
        <Link to="/dokumente" data-testid="qa-dokument" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-violet-600/15 border border-violet-500/30 text-violet-300 hover:bg-violet-600/25 text-xs font-medium transition-colors">
          <Upload className="w-3.5 h-3.5" /> Dokument
        </Link>
      )}
      {canKarte && (
        <Link to="/karten" data-testid="qa-karte" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-cyan-600/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-600/25 text-xs font-medium transition-colors">
          <CreditCard className="w-3.5 h-3.5" /> Karte
        </Link>
      )}
      {canUebergabe && (
        <Link to="/fuhrpark/uebergaben" data-testid="qa-uebergabe" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-rose-600/15 border border-rose-500/30 text-rose-300 hover:bg-rose-600/25 text-xs font-medium transition-colors">
          <ArrowLeftRight className="w-3.5 h-3.5" /> KFZ-Übergabe
        </Link>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, subtitle, tone = "zinc", testid, link }) {
  const tones = {
    zinc: "border-zinc-800 bg-zinc-950 text-zinc-100",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    red: "border-red-500/40 bg-red-500/10 text-red-100",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
    sky: "border-sky-500/40 bg-sky-500/10 text-sky-100",
  };
  const Wrap = link ? Link : "div";
  return (
    <Wrap to={link} className={`block rounded-lg border p-4 transition-colors ${tones[tone] || tones.zinc} ${link ? "hover:border-zinc-600" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-3xl font-display font-bold tabular-nums">{value ?? "—"}</div>
      {subtitle && <div className="text-[10px] text-zinc-500 mt-0.5">{subtitle}</div>}
    </Wrap>
  );
}

function UrgencyBadge({ urgency, days }) {
  const conf = {
    red: { bg: "bg-red-500/15 text-red-300 border-red-500/30", label: days < 0 ? `${Math.abs(days)}d überfällig` : `${days}d` },
    yellow: { bg: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: `${days}d` },
    green: { bg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: `${days}d` },
  }[urgency] || { bg: "bg-zinc-800 text-zinc-400 border-zinc-700", label: `${days}d` };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono font-semibold ${conf.bg}`}>{conf.label}</span>;
}

function Card({ title, icon: Icon, testid, children }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={testid}>
      <header className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
        <Icon className="w-4 h-4 text-amber-500" />
        <h3 className="font-display font-semibold text-sm text-zinc-100">{title}</h3>
      </header>
      <div>{children}</div>
    </div>
  );
}

function QueueRow({ label, count, link }) {
  return (
    <Link to={link} className="px-4 py-2.5 flex items-center justify-between hover:bg-zinc-900/40 border-b border-zinc-800 last:border-0">
      <span className="text-sm text-zinc-200">{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-xs font-mono tabular-nums px-2 py-0.5 rounded ${count > 0 ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-500"}`}>
          {count}
        </span>
        <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />
      </div>
    </Link>
  );
}

function Stat({ label, value, sub, tone = "zinc" }) {
  const tones = { zinc: "text-zinc-100", red: "text-red-300" };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// Phase 5Z.36 Phase 3 — HR-Manager-Karte für das Admin-Dashboard
function HrManagerCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/hr/dashboard-stats").then(r => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const v = stats.vacation;
  const ab = stats.absences;
  const ot = stats.overtime;
  const docs = stats.documents;
  // Build a list of warnings to surface
  const warns = [];
  if (ab.pending_requests > 0)
    warns.push({ key: "pending", color: "amber", text: `${ab.pending_requests} offene Anträge` });
  if (v.employees_with_expiring_carry_over > 0)
    warns.push({ key: "carry", color: "rose", text: `${v.employees_with_expiring_carry_over} MA · Resturlaub läuft ab` });
  if (docs.employees_with_expiring_docs_60d > 0)
    warns.push({ key: "docs", color: "amber", text: `${docs.employees_with_expiring_docs_60d} MA · Dokumente ablaufend` });
  if (ab.active_sick_leave > 0)
    warns.push({ key: "sick", color: "rose", text: `${ab.active_sick_leave} aktuell krank` });

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid="dashboard-hr-card">
      <header className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-500" />
          <h2 className="font-display font-semibold text-zinc-100">HR · Personal-Übersicht</h2>
        </div>
        <Link to="/hr" className="text-[11px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1">
          Zum HR-Hub <ArrowRight className="w-3 h-3" />
        </Link>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4">
        <Stat label="Urlaub Saldo" value={v.total_remaining_days.toFixed(0)} sub={`${stats.employees_active} aktive MA`} />
        <Stat label="Resturlaub" value={`+${v.carry_over_total.toFixed(0)}`} sub={v.carry_over_expiring_90d > 0 ? `${v.carry_over_expiring_90d.toFixed(0)} läuft ab` : "Vorjahres-Tage"} tone={v.carry_over_expiring_90d > 0 ? "red" : "zinc"} />
        <Stat label="Überstunden" value={`${ot.total_balance_hours.toFixed(0)} h`} sub="Gesamt-Saldo" />
        <Stat label="Krank YTD" value={ab.sick_days_year_to_date} sub={`${ab.active_sick_leave} aktuell aktiv`} />
      </div>
      {warns.length > 0 && (
        <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-900/40 flex flex-wrap gap-2" data-testid="dashboard-hr-warnings">
          {warns.map(w => (
            <span key={w.key} data-testid={`dashboard-hr-warn-${w.key}`}
              className={`text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1.5 ${
                w.color === "rose"
                  ? "bg-rose-950/40 border-rose-800/60 text-rose-200"
                  : "bg-amber-950/40 border-amber-800/60 text-amber-200"}`}>
              <AlertTriangle className="w-3 h-3" />
              {w.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

